import { fetchSiteDetails } from '@/remote/dashboard/site-details';
import { QUERY_KEYS } from '@/remote/query-key';
import type { SiteDetails } from '@/types/site';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseSiteDetailsOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

interface UseSiteDetailsReturn {
  data: SiteDetails | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  actions: {
    refetch: () => void;
    invalidate: () => void;
  };
}

export const useSiteDetails = (
  siteId: string,
  options: UseSiteDetailsOptions = {}
): UseSiteDetailsReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 60 * 5, // 🚀 5분으로 단축 - 적당한 캐싱
    gcTime = 1000 * 60 * 15, // 🚀 15분으로 단축 - 메모리 절약
  } = options;

  const query = useQuery<SiteDetails>({
    queryKey: QUERY_KEYS.dashboard.siteDetails(siteId),
    queryFn: () => fetchSiteDetails(siteId),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { refetch } = query;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.siteDetails(siteId),
    });
  }, [queryClient, siteId]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    actions: {
      refetch,
      invalidate,
    },
  };
};
